import { z } from 'zod';
import {
  defaultLongRentMinutes,
  defaultOutageMinutes,
  defaultRetryWindowMinutes,
  defaultScanWindowMinutes,
  maxReportStations,
  reportBlockIds,
  reportScopeKinds,
  reportVariants,
  stationReportParams,
} from '@qronstat/shared';
import type {
  DateRangeSpec,
  ParsedStationReportParams,
  ReportScopeKind,
  ReportVariant,
  StationReportParams,
} from '@qronstat/shared';

export const stationReportSearch = z.object({
  sc: z.enum(reportScopeKinds).optional().catch(undefined),
  ids: z.array(z.string().min(1).max(100)).max(maxReportStations).optional().catch(undefined),
  gt: z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,40}$/)
    .optional()
    .catch(undefined),
  lbl: z.string().max(200).optional().catch(undefined),
  v: z.enum(reportVariants).optional().catch(undefined),
  b: z.array(z.enum(reportBlockIds)).max(reportBlockIds.length).optional().catch(undefined),
  om: z.number().int().min(1).max(43_200).optional().catch(undefined),
  lm: z.number().int().min(1).max(43_200).optional().catch(undefined),
  rw: z.number().int().min(1).max(1440).optional().catch(undefined),
  sw: z.number().int().min(1).max(1440).optional().catch(undefined),
});
export type StationReportSearch = z.infer<typeof stationReportSearch>;

export interface ReportTarget {
  scope: ReportScopeKind;
  ids: string[];
  label?: string;
  groupType?: string;
}

export function reportHref(
  target: ReportTarget,
  extra: { period?: DateRangeSpec; variant?: ReportVariant } = {},
): { to: '/reports/stations'; search: Record<string, unknown> } {
  return {
    to: '/reports/stations',
    search: {
      sc: target.scope,
      ids: target.ids,
      ...(target.groupType ? { gt: target.groupType } : {}),
      ...(target.label ? { lbl: target.label } : {}),
      ...(extra.period ? { period: extra.period } : {}),
      ...(extra.variant && extra.variant !== 'full' ? { v: extra.variant } : {}),
    },
  };
}

export function paramsFromSearch(
  search: StationReportSearch,
  screen: { period: DateRangeSpec; realOnly: boolean; timeMode: 'msk' | 'local' },
): ParsedStationReportParams | null {
  if (!search.ids?.length) return null;
  const input: StationReportParams = {
    scope: search.sc ?? 'stations',
    ids: search.sc && search.sc !== 'stations' ? search.ids.slice(0, 1) : search.ids,
    ...(search.gt ? { groupType: search.gt } : {}),
    period: screen.period,
    realOnly: screen.realOnly,
    timeMode: screen.timeMode,
    variant: search.v ?? 'full',
    ...(search.b ? { blocks: search.b } : {}),
    outageMinutes: search.om ?? defaultOutageMinutes,
    longRentMinutes: search.lm ?? defaultLongRentMinutes,
    retryWindowMinutes: search.rw ?? defaultRetryWindowMinutes,
    scanWindowMinutes: search.sw ?? defaultScanWindowMinutes,
    ...(search.lbl ? { label: search.lbl } : {}),
  };
  const parsed = stationReportParams.safeParse(input);
  return parsed.success ? parsed.data : null;
}
